import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs, } from '@datadog/browser-logs';
import config from '../global/env.config';
import { GrowthBookFeatures, hasGrowthBookFeature } from '../modules/core';
import { redactSensitiveFields } from '../common/utils/redaction';
import { isPatientContext } from '../vendor/api';
import { deleteCookie, getCookieSubdomainString } from './cookie';
const { env, datadogConfig, version, commitSha, apiUrl, lb4ApiUrl } = config;
const IS_DATADOG_ENABLED = env !== 'development';
const SERVICE_NAME = 'doxyme-frontend';
/**
 * Shared telemetry proxy URL for Browser RUM and Browser Logs (v2 parity).
 * Name `DATADOG_RUM_PROXY_URL` / `datadogConfig.rumProxyUrl` kept for backwards compatibility.
 */
function getDatadogTelemetryProxyUrl() {
    const c = config;
    const raw = c.datadogConfig?.rumProxyUrl ?? c.DATADOG_RUM_PROXY_URL;
    const trimmed = raw?.trim() ?? '';
    return trimmed === '' ? undefined : trimmed;
}
/** Vonage OpenTok SDK telemetry; failures are non-actionable and do not affect video. */
const VONAGE_LOGGING_URL = 'hlg.tokbox.com/prod/logging/';
/** OpenTok SDK debug console prefix (e.g. OpenTok:RaptorSocket:error). */
const VONAGE_SDK_CONSOLE_PREFIX = 'OpenTok:';
const isVonageSdkTelemetryNetworkError = (log) => log.origin === 'network' &&
    (log.http?.url.includes(VONAGE_LOGGING_URL) ?? false);
/** SDK WebSocket close fallback (code 4020) and other internal debug console noise. */
const isVonageSdkConsoleError = (log) => log.origin === 'console' && log.message.includes(VONAGE_SDK_CONSOLE_PREFIX);
const isVonageSdkNoiseLog = (log) => isVonageSdkTelemetryNetworkError(log) || isVonageSdkConsoleError(log);
const isLogRedactionEnabled = () => {
    const growthbookInstance = typeof window !== 'undefined'
        ? window.growthbookInstance
        : undefined;
    if (typeof growthbookInstance === 'undefined') {
        return false;
    }
    return hasGrowthBookFeature(GrowthBookFeatures.OgFrontendLogRedaction);
};
const initializeRum = () => {
    /**
     * Delete previous multiple `_dd_s` cookie keys set by DataDog in the Core-V2 app
     */
    deleteCookie({
        name: '_dd_s',
    });
    deleteCookie({
        name: '_dd_s',
        domain: getCookieSubdomainString(),
    });
    if (IS_DATADOG_ENABLED) {
        const telemetryProxyUrl = getDatadogTelemetryProxyUrl();
        const proxyOption = telemetryProxyUrl !== undefined ? { proxy: telemetryProxyUrl } : {};
        const commonOptions = {
            service: SERVICE_NAME,
            env: datadogConfig?.env ?? env,
            // Specify a version number to identify the deployed version of your application in Datadog
            version: String(version),
        };
        datadogRum.init({
            ...commonOptions,
            ...proxyOption,
            // ---
            // Please do not increase `sessionSampleRate` or `sessionReplaySampleRate`
            // values without first consulting Ben Anderson-Dukes and Myles Sanigar.
            //
            // Note: these numbers are percentages: 0-100
            //
            // Note: the actual session-replay sample rate is calculated as
            // `sessionSampleRate`% * `sessionReplaySampleRate`%
            sessionSampleRate: 0,
            sessionReplaySampleRate: 0,
            // ---
            trackUserInteractions: true,
            trackSessionAcrossSubdomains: true,
            defaultPrivacyLevel: 'mask-user-input',
            allowedTracingUrls: [window.location.origin, apiUrl, lb4ApiUrl],
            sessionPersistence: 'local-storage',
            beforeSend(event) {
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (event?.view?.referrer) {
                    const referrer = new URL(event.view.referrer);
                    if (referrer.pathname === '/reset-password') {
                        referrer.searchParams.delete('token');
                        referrer.searchParams.set('username', '[username]');
                        event.view.referrer = referrer.href;
                    }
                }
                // redact Patient Username
                if (event.view.url) {
                    const url = new URL(event.view.url);
                    if (url.searchParams.has('username')) {
                        url.searchParams.set('username', '[username]');
                        event.view.url = url.href;
                    }
                }
                return true;
            },
            ...datadogConfig,
        });
        datadogRum.setGlobalContextProperty('commit_sha', commitSha);
        datadogRum.setGlobalContextProperty('frontend_version', version);
        datadogRum.setGlobalContextProperty('is_apollo', false);
        datadogLogs.init({
            ...datadogConfig,
            ...commonOptions,
            ...proxyOption,
            forwardErrorsToLogs: true,
            forwardConsoleLogs: ['info', 'warn', 'error'],
            sessionSampleRate: 100,
            sessionPersistence: 'local-storage',
            beforeSend: (log) => {
                if (isVonageSdkNoiseLog(log)) {
                    return false;
                }
                if (isLogRedactionEnabled()) {
                    redactSensitiveFields(log, isPatientContext());
                }
                return true;
            },
        });
        datadogLogs.setGlobalContextProperty('commit_sha', commitSha);
        datadogLogs.setGlobalContextProperty('frontend_version', version);
        datadogLogs.setGlobalContextProperty('is_apollo', false);
    }
};
/**
 * Create new datadog logger. We do not create logger if datadog is not initialised, it will not work
 */
export const createLogger = (name, conf) => {
    return IS_DATADOG_ENABLED ? datadogLogs.createLogger(name, conf) : undefined;
};
// Initialize DataDogRum on app load
initializeRum();
export default datadogRum;
